
/**
 * Represents a top anchored navigation bar component.
 * This component is responsible for displaying the top bar of the Business Hub application.
 */
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType, InteractionType, AccountInfo, IdTokenClaims, PromptValue } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Store, select } from '@ngrx/store';
import { AppState } from 'src/app/services/models/application-details.model';
import { selectCurrentUserRole, selectAppList } from 'src/app/store/app-state.selectors';
import { setCurrentUser } from 'src/app/store/app-state.actions';
import { AppUserType } from 'src/app/services/models/app-user.model';
import { AppSelectionService } from 'src/app/services/app-selection.service';
import { AppUserService } from 'src/app/services/app-user.service';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
  emails?: string[],
};
@Component({
    selector: 'bhub-top-bar',
    templateUrl: './bhub-top-bar.component.html',
    styleUrls: ['./bhub-top-bar.component.scss'],
    standalone: false
})
export class BhubTopBarComponent implements OnInit, OnDestroy {
  loginDisplay = false;
  currentUserRole: AppUserType | null = null;
  appCount = 0;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private store: Store<AppState>,
    private appSelection: AppSelectionService,
    private appUserService: AppUserService
    )
    {

    }


  ngOnInit(): void {
    // Subscribe to user role changes
    this.store.pipe(
      select(selectCurrentUserRole),
      takeUntil(this._destroying$)
    ).subscribe(role => {
      this.currentUserRole = role;
    });

    // Track how many apps the user belongs to — the app switcher only shows for
    // users with more than one.
    this.store.pipe(
      select(selectAppList),
      takeUntil(this._destroying$)
    ).subscribe(appList => {
      this.appCount = appList?.length ?? 0;
    });

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
        )
        .subscribe((result: EventMessage) => {
            if (this.authService.instance.getAllAccounts().length === 0) {
                window.location.pathname = "/";
            } else {
                this.setLoginDisplay();
            }
        });


    this.msalBroadcastService.inProgress$
        .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            takeUntil(this._destroying$)
        )
        .subscribe(() => {
            this.setLoginDisplay();
            this.checkAndSetActiveAccount();
            this.ensureAppListLoaded();
        })

    this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
                || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
                || msg.eventType === EventType.SSO_SILENT_SUCCESS),
            takeUntil(this._destroying$)
        )
        .subscribe((result: EventMessage) => {

            let payload = result.payload as AuthenticationResult;
            let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

            if (idtoken.acr === environment.b2cPolicies.names.signUpSignIn || idtoken.tfp === environment.b2cPolicies.names.signUpSignIn) {
                this.authService.instance.setActiveAccount(payload.account);
            }

            this.dispatchCurrentUserFromClaims(idtoken);

            /**
             * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
             * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
             * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
             */
            if (idtoken.acr === environment.b2cPolicies.names.editProfile || idtoken.tfp === environment.b2cPolicies.names.editProfile) {

                // retrieve the account from initial sing-in to the app
                const originalSignInAccount = this.authService.instance.getAllAccounts()
                    .find((account: AccountInfo) =>
                            account.idTokenClaims?.oid === idtoken.oid
                            && account.idTokenClaims?.sub === idtoken.sub
                            && ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr === environment.b2cPolicies.names.signUpSignIn
                                || (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp === environment.b2cPolicies.names.signUpSignIn)
                        );

                let signUpSignInFlowRequest: PopupRequest = {
                    scopes: [...environment.apiConfig.scopes],
                    authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                    account: originalSignInAccount,
                    prompt: PromptValue.NONE
                };

                // To get the updated account information
                this.authService.acquireTokenPopup(signUpSignInFlowRequest);
            }

            /**
             * Below we are checking if the user is returning from the reset password flow.
             * If so, we will ask the user to reauthenticate with their new password.
             * If you do not want this behavior and prefer your users to stay signed in instead,
             * you can replace the code below with the same pattern used for handling the return from
             * profile edit flow (see above ln. 74-92).
             */
            if (idtoken.acr === environment.b2cPolicies.names.resetPassword || idtoken.tfp === environment.b2cPolicies.names.resetPassword) {
                let signUpSignInFlowRequest: RedirectRequest | PopupRequest  = {
                    authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                    scopes: [...environment.apiConfig.scopes],
                    prompt: PromptValue.LOGIN // force user to reauthenticate with their new password
                };

                this.login(signUpSignInFlowRequest);
            }

            return result;
        });

    this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
            takeUntil(this._destroying$)
        )
        .subscribe((result: EventMessage) => {
            // Check for forgot password error
            // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
            if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
                let resetPasswordFlowRequest: RedirectRequest | PopupRequest  = {
                    authority: environment.b2cPolicies.authorities.resetPassword.authority,
                    scopes: [],
                };

                this.login(resetPasswordFlowRequest);
            };
        });
  }

  setLoginDisplay() {
      this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
      /**
       * If no active account set but there are accounts signed in, sets first account to active account
       * To use active account set here, subscribe to inProgress$ first in your component
       * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
       */
      let activeAccount = this.authService.instance.getActiveAccount();

      if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
          let accounts = this.authService.instance.getAllAccounts();
          this.authService.instance.setActiveAccount(accounts[0]);
          activeAccount = accounts[0];
      }

      // Re-hydrate currentUser from the cached account on page reload (when MSAL
      // restores an existing account without firing a new LOGIN_SUCCESS event).
      if (activeAccount?.idTokenClaims) {
          this.dispatchCurrentUserFromClaims(activeAccount.idTokenClaims as IdTokenClaimsWithPolicyId);
      }
  }

  /**
   * The app switcher needs to know how many applications the user belongs to,
   * but a hard reload (a refresh, or the reload that follows an app switch)
   * empties the store and nothing else refetches it — BoardAccessGuard resolves
   * the current app from localStorage without calling the API. So ask for the
   * list here. getUserAppList() serves it from the store when it's already
   * there, making this at most one request per page load.
   *
   * Called only once MSAL reports no interaction in progress, so the request
   * always carries a token.
   */
  private ensureAppListLoaded(): void {
      if (!this.loginDisplay) {
          return;
      }

      this.appUserService.getUserAppList()
          .pipe(takeUntil(this._destroying$))
          .subscribe({
              error: (error: unknown) => {
                  // Only drives whether the switcher is shown — nothing to surface.
                  console.warn('Top bar: could not load the user app list', error);
              }
          });
  }

  private dispatchCurrentUserFromClaims(claims: IdTokenClaimsWithPolicyId): void {
      const email = claims.emails?.[0];
      const name = claims.name;
      if (email && name) {
          this.store.dispatch(setCurrentUser({ user: { email, name } }));
      }
  }

  loginRedirect() {
      if (this.msalGuardConfig.authRequest) {
          this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
          this.authService.loginRedirect();
      }
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
          if (this.msalGuardConfig.authRequest) {
              this.authService.loginPopup({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as PopupRequest)
                  .subscribe((response: AuthenticationResult) => {
                      this.authService.instance.setActiveAccount(response.account);
                  });
          } else {
              this.authService.loginPopup(userFlowRequest)
                  .subscribe((response: AuthenticationResult) => {
                      this.authService.instance.setActiveAccount(response.account);
                  });
          }
      } else {
          if (this.msalGuardConfig.authRequest) {
              this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
          } else {
              this.authService.loginRedirect(userFlowRequest);
          }
      }
  }

  logout() {
      // Drop the persisted app choice so a different user signing in on this
      // browser doesn't inherit it (the guard also re-validates against the
      // fetched app list).
      this.appSelection.clearPersistedApp();
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
          this.authService.logoutPopup({
              mainWindowRedirectUri: environment.msalConfig.auth.redirectUri
          });
      } else {
          this.authService.logoutRedirect();
      }
  }

  editProfile() {
      let editProfileFlowRequest: RedirectRequest | PopupRequest  = {
          authority: environment.b2cPolicies.authorities.editProfile.authority,
          scopes: [],
      };

      this.login(editProfileFlowRequest);
  }

  // Check if user has admin access
  hasAdminAccess(): boolean {
    return this.currentUserRole === AppUserType.Admin || this.currentUserRole === AppUserType.Owner;
  }

  ngOnDestroy(): void {
      this._destroying$.next(undefined);
      this._destroying$.complete();
  }
}
